// user-form.component.ts
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Subject, firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HttpErrorResponse } from '@angular/common/http';

// Core Services
import { ApiService, SignupRequestPayload, Region, UserRole, Country, OnboardedUser, UserUpdatePayload } from "@core/services/api/api.service";
import { FirebaseStorageService } from "@core/services/api/firebase-storage.service";
import { UploadedFile } from "@core/models/file-upload.interface";
import { UserEventsService } from '@core/services/events/user-events.service';

// PrimeNG Modules
import { MessageService, ConfirmationService } from "primeng/api";
import { CalendarModule } from 'primeng/calendar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { getApiUrl } from '@shared/utils/app-util';

@Component({
  selector: "app-user-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CalendarModule,
    ConfirmDialogModule,
    DialogModule,
    DropdownModule,
    MultiSelectModule,
    TooltipModule,
    InputTextModule,
    ButtonModule
  ],
  templateUrl: "./user-form.component.html",
  styleUrls: ["./user-form.component.scss"],
  providers: [MessageService, DatePipe, ConfirmationService]
})
export class UserFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible: boolean = false;
  @Input() editMode: boolean = false;
  @Input() userData: OnboardedUser | null = null;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeDialog = new EventEmitter<void>();
  @Output() userCreated = new EventEmitter<any>();
  @Output() userUpdated = new EventEmitter<any>();
  @Output() viewCertificateEvent = new EventEmitter<{ url: string; fileName: string }>();

  @ViewChild('fileInput') fileInput!: ElementRef;

  userForm!: FormGroup;
  formSubmitted: boolean = false;
  isSubmitting: boolean = false;

  roles: UserRole[] = [];
  countries: Country[] = [];
  regions: Region[] = [];
  allCountries: Country[] = [];
  filteredResponsibleCountries: Country[] = [];
  
  // File upload properties
  uploadedFile: UploadedFile | null = null;
  private uploadedPath: string | null = null;

  private readonly unsubscribe$ = new Subject<void>();
  // initialFormState is mutated; keep it mutable
  private initialFormState: any;
  // isFormInitialized toggles once; keep as mutable boolean
  private isFormInitialized = false;

  // Lookup load flags and pending patch control
  private rolesLoaded = false;
  private countriesLoaded = false;
  private regionsLoaded = false;
  private pendingPatch = false;

  // Dates for minDate
  today: Date = new Date();
  tomorrow: Date = new Date();

  constructor(
  private readonly fb: FormBuilder,
  private readonly apiService: ApiService,
  private readonly messageService: MessageService,
  private readonly datePipe: DatePipe,
  private readonly confirmationService: ConfirmationService,
  private readonly firebaseStorage: FirebaseStorageService,
  private readonly userEvents: UserEventsService
  ) {}

  ngOnInit(): void {
    // Normalize today to start of day and compute tomorrow
    this.today.setHours(0,0,0,0);
    this.tomorrow = new Date(this.today.getTime());
    this.tomorrow.setDate(this.tomorrow.getDate() + 1);
    this.initializeForm();
    this.loadDropdownData();
    this.setupDependentDropdowns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true && this.editMode && this.userData) {
      // Defer patch until lookups are loaded
      if (this.rolesLoaded && this.countriesLoaded && this.regionsLoaded) {
        this.patchFormWithUserData(this.userData);
      } else {
        this.pendingPatch = true;
      }
    } else if (changes['visible'] && changes['visible'].currentValue === false) {
      this.resetForm();
    }
  }

  initializeForm(): void {
    this.userForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      roleType: [null, Validators.required],
      // Default Status to Active in create mode
      status: ['Active', Validators.required],
      userCountry: [null, Validators.required],
      responsibleRegion: [null, Validators.required],
      responsibleCountry: [{ value: [], disabled: true }, Validators.required],
      companyName: ['', Validators.required],
      // End date is optional for both create and edit modes
      endDate: [null]
    });
  }

  patchFormWithUserData(user: OnboardedUser): void {
    const role = this.roles.find(r => r.id === user.role_id);
    const userCountry = this.countries.find(c => c.id === user.country_id);
    const responsibleRegion = this.regions.find(r => r.id === (user as any).assigned_region_id);

    if (responsibleRegion) {
      this.userForm.get('responsibleRegion')?.setValue(responsibleRegion, { emitEvent: false });
      this.filteredResponsibleCountries = this.allCountries.filter((country: Country) => (country as any)?.region?.id === responsibleRegion.id);
      this.userForm.get('responsibleCountry')?.enable({ emitEvent: false });
    } else {
      this.userForm.get('responsibleCountry')?.disable({ emitEvent: false });
      this.filteredResponsibleCountries = [];
    }

    const responsibleCountries = ((user as any).assigned_country_ids || [])
      .map((id: number) => this.allCountries.find(c => c.id === id))
      .filter((c: Country | undefined): c is Country => c !== undefined);

    const statusValue = this.mapApiStatusToUi((user as any).status);

    this.userForm.patchValue({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      roleType: role,
      status: statusValue,
      userCountry: userCountry,
      responsibleRegion: responsibleRegion,
      companyName: user.company_name,
      // Parse active_till robustly: ensure a Date object is provided even when API omits timezone
      endDate: user.active_till ? this.parseApiDateToDate(user.active_till) : null
    }, { emitEvent: false });

    // Apply countries after options are filtered (next microtask)
    Promise.resolve().then(() => {
      this.userForm.get('responsibleCountry')?.setValue(responsibleCountries, { emitEvent: false });
      this.userForm.get('responsibleCountry')?.updateValueAndValidity({ emitEvent: false });
    });

    // Ensure email remains editable
    this.userForm.get('email')?.enable({ emitEvent: false });

    this.userForm.get('responsibleRegion')?.updateValueAndValidity({ emitEvent: false });
    this.userForm.get('responsibleCountry')?.updateValueAndValidity({ emitEvent: false });

    // If user has a certificate_url, show it in the UI
    if (user.certificate_url) {
      this.uploadedFile = {
        id: this.generateFileId(),
        name: this.extractFileNameFromUrl(user.certificate_url),
        file: new File([], this.extractFileNameFromUrl(user.certificate_url)), // Dummy file object
        url: user.certificate_url,
        uploading: false,
        uploaded: true,
        progress: 100
      };
      // Set the uploaded path from the existing URL for future reference
      this.uploadedPath = this.extractPathFromUrl(user.certificate_url);
    }

    this.initialFormState = this.userForm.getRawValue();
    this.initialFormState = JSON.parse(JSON.stringify(this.initialFormState));
  }

  private extractFileNameFromUrl(url: string): string {
    // Extract filename from URL or return a default name
    const parts = url.split('/');
    return parts[parts.length - 1] || 'certificate.pdf';
  }

  loadDropdownData(): void {
    this.apiService.getRegions().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { regions: Region[] }) => { this.regions = response.regions; this.regionsLoaded = true; this.tryDeferredPatch(); },
      error: (err: HttpErrorResponse) => console.error("Failed to load regions", err),
    });

    this.apiService.getCountries().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { countries: Country[] }) => {
        this.allCountries = response.countries;
        this.countries = [...this.allCountries];
        this.countriesLoaded = true;
        this.tryDeferredPatch();
      },
      error: (err: HttpErrorResponse) => console.error("Failed to load countries", err),
    });

    this.apiService.getUserRoles().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { user_roles: UserRole[] }) => { this.roles = response.user_roles; this.rolesLoaded = true; this.tryDeferredPatch(); },
      error: (err: HttpErrorResponse) => console.error("Failed to load user roles", err),
    });
  }

  private tryDeferredPatch(): void {
    if (this.pendingPatch && this.rolesLoaded && this.countriesLoaded && this.regionsLoaded && this.visible && this.editMode && this.userData) {
      this.pendingPatch = false;
      this.patchFormWithUserData(this.userData);
    }
  }

  private mapApiStatusToUi(api: string | null | undefined): 'Active' | 'Inactive' | null {
    if (!api) return null;
    const s = String(api).toLowerCase();
    if (s.includes('inactive') || s.includes('deactivated')) return 'Inactive';
    return 'Active';
  }

  private mapUiStatusToApi(ui: string | null | undefined): 'active' | 'inactive' | undefined {
    if (!ui) return undefined;
    return String(ui).toLowerCase() === 'inactive' ? 'inactive' : 'active';
  }

  setupDependentDropdowns(): void {
    const responsibleRegionControl = this.userForm.get('responsibleRegion');
    const responsibleCountryControl = this.userForm.get('responsibleCountry');

    responsibleRegionControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedRegion: Region | null) => {
        responsibleCountryControl?.reset([]);
        this.filteredResponsibleCountries = [];

        if (selectedRegion) {
          this.filteredResponsibleCountries = this.allCountries.filter(
            country => country.region.id === selectedRegion.id
          );
          responsibleCountryControl?.enable();
        } else {
          responsibleCountryControl?.disable();
        }
      });
  }

  // File upload methods
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (this.validateFile(file)) {
        this.addFileToUploadQueue(file);
      }
      input.value = ''; // Reset file input
    }
  }

  private validateFile(file: File): boolean {
    const allowedTypes = ['application/pdf', 'application/msword', 'image/png'];
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid File Type',
        detail: 'Only PDF, DOC, and PNG files are allowed.',
        life: 5000
      });
      return false;
    }

    if (file.size > maxSizeInBytes) {
      this.messageService.add({
        severity: 'error',
        summary: 'File Too Large',
        detail: 'Maximum file size is 5MB.',
        life: 5000
      });
      return false;
    }

    return true;
  }

  private addFileToUploadQueue(file: File): void {
    // If there's already an uploaded file, replace it
    if (this.uploadedFile?.uploaded && this.uploadedFile?.url) {
      // If in edit mode and replacing existing file, we might want to delete the old one
      // This could be optional based on your requirements
      if (this.editMode) {
        this.removeFileFromStorage();
      }
    }

  this.uploadedPath = null;
  this.uploadedFile = {
      id: this.generateFileId(),
      name: file.name,
      file: file,
      uploading: false,
      uploaded: false,
      progress: 0
    };
  }

  private generateFileId(): string {
    return Date.now().toString() + Math.random().toString(36).slice(2, 11);
  }

  confirmDeleteFile(): void {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: 'Are you sure you want to remove this file?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        this.removeFile();
      }
    });
  }

  private removeFile(): void {
    if (this.uploadedFile) {
  this.removeFileFromStorage();
  this.uploadedPath = null;
      this.uploadedFile = null;
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'File removed successfully.',
        life: 3000
      });
    }
  }

  private removeFileFromStorage(): void {
    // Only attempt to delete from storage if the file was uploaded and we have a URL
    if (this.uploadedFile?.uploaded && this.uploadedFile?.url) {
      // Prefer stored path; fallback to path derived from URL
      let filePath = this.uploadedPath || '';
      if (!filePath) {
        const re = /\/o\/([^?]+)/;
        const match = re.exec(this.uploadedFile.url);
        const captured = match?.[1];
        if (captured) {
          filePath = decodeURIComponent(captured);
        } else {
          const email = this.userForm.get('email')?.value || 'temp_user';
          filePath = this.firebaseStorage.generateFilePath(email, this.uploadedFile.name);
        }
      }

      this.firebaseStorage.deleteFile(filePath).subscribe({
        next: () => {},
        error: (error) => console.error('Failed to delete file from Firebase:', error)
      });
    }
  }

  async uploadCertificate(): Promise<{ url: string; path: string;}> {
    // If no file selected or already uploaded, return empty string or existing URL
    if (!this.uploadedFile || !this.uploadedFile.file || this.uploadedFile.file.size === 0) {
      // If in edit mode and no new file is selected, but there was an existing one, return its details
      if (this.editMode && this.userData?.certificate_url) {
        // Extract path from the existing URL if uploadedPath is not set
        const existingPath = this.uploadedPath || this.extractPathFromUrl(this.userData.certificate_url) || '';
        return { url: this.userData.certificate_url, path: existingPath };
      }
      return { url: '', path: '' };
    }

    // If the file was already uploaded and we have its details, return them
    if (this.uploadedFile.uploaded && this.uploadedFile.url && this.uploadedPath) {
      return { url: this.uploadedFile.url, path: this.uploadedPath };
    }

    // Start upload process
    this.uploadedFile.uploading = true;
    this.uploadedFile.progress = 0;
    this.uploadedFile.error = undefined; // Clear previous errors

    try {
      const email = this.userForm.get('email')?.value;
      // Use a temporary ID if email is not yet available or valid (e.g., in create mode)
      const resourceId = email && this.userForm.get('email')?.valid ? email : 'temp_user_certificate';
      const resourceType = 'certificates'; // Consistent category for user certificates

      // Use the authenticated upload method
      const downloadUrl = await this.firebaseStorage.uploadAuthFile(this.uploadedFile.file, resourceType, resourceId);

      // Extract path and bucket from the download URL
      const filePath = this.extractPathFromUrl(downloadUrl);
      const bucketName = this.extractBucketFromUrl(downloadUrl);

      if (!downloadUrl || !filePath || !bucketName) {
        throw new Error('File upload failed - missing URL, path, or bucket.');
      }

      this.uploadedFile.uploading = false;
      this.uploadedFile.uploaded = true;
      this.uploadedFile.url = downloadUrl;
      this.uploadedFile.progress = 100;
      this.uploadedPath = filePath;

      this.messageService.add({
        severity: 'success',
        summary: 'Upload Success',
        detail: 'Certificate uploaded successfully',
        life: 3000
      });

      return { url: downloadUrl, path: filePath };
    } catch (error: any) {
      if (this.uploadedFile) {
        this.uploadedFile.uploading = false;
        this.uploadedFile.error = error.message || 'Upload failed';
        this.uploadedFile.progress = 0;
      }

      this.messageService.add({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.message || 'Certificate upload failed. Please try again.',
        life: 5000
      });

      throw error;
    }
  }

  private trimFormValues(formValue: any): any {
    const trimmedValue: any = {};
    for (const key in formValue) {
      if (formValue.hasOwnProperty(key)) {
        const value = formValue[key];
        if (typeof value === 'string') {
          trimmedValue[key] = value.trim();
        } else if (Array.isArray(value)) {
          trimmedValue[key] = value.map(item => typeof item === 'string' ? item.trim() : item);
        } else {
          trimmedValue[key] = value;
        }
      }
    }
    return trimmedValue;
  }

  async onSubmit(): Promise<void> {
    this.formSubmitted = true;
    this.isSubmitting = true;

    const responsibleCountryControl = this.userForm.get('responsibleCountry');
    if (responsibleCountryControl?.disabled) {
      responsibleCountryControl.markAsTouched();
    }

    // Enforce strictly-future end date (not today)
    const endDateCtrl = this.userForm.get('endDate');
    const endDateVal: any = endDateCtrl?.value;
    if (endDateVal instanceof Date) {
      const chosen = new Date(endDateVal);
      chosen.setHours(0,0,0,0);
      const today = new Date(); today.setHours(0,0,0,0);
      if (chosen <= today) {
        this.messageService.add({ severity: 'warn', summary: 'Invalid Date', detail: 'End Date must be in the future.', life: 4000 });
        this.isSubmitting = false;
        return;
      }
    }

    // Check if form is valid; in create mode require a certificate, in edit mode do not
    if (this.userForm.invalid || (!this.editMode && !this.uploadedFile)) {
      this.userForm.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: (!this.editMode && !this.uploadedFile)
          ? 'Please upload a certificate file'
          : 'Please fill all required fields and correct any errors.',
        life: 5000
      });
      const firstInvalidControl = document.querySelector('.ng-invalid');
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      this.isSubmitting = false;
      return;
    }

    try {
      // Upload certificate if available
      const { url: certificateUrl, path: uploadedPath } = await this.uploadCertificate();
      const formValue = this.trimFormValues(this.userForm.getRawValue());

      // Normalize end date: format selected calendar date as UTC midnight string (preserve calendar day)
      const formattedEndDate: string | undefined = this.formatCalendarDateToUtcString(formValue.endDate);

      if (this.editMode && this.userData) {
        const updatedFields: Partial<UserUpdatePayload> = {};
        let isFormDirty = false;

        for (const key in formValue) {
          if (formValue.hasOwnProperty(key)) {
            const initialValue = this.initialFormState[key];
            const currentValue = formValue[key];

            if (key === 'roleType') {
              if (initialValue?.id !== currentValue?.id) { updatedFields.role_id = currentValue.id; isFormDirty = true; }
            } else if (key === 'userCountry') {
              if (initialValue?.id !== currentValue?.id) { updatedFields.country_id = currentValue.id; isFormDirty = true; }
            } else if (key === 'responsibleRegion') {
              if (initialValue?.id !== currentValue?.id) { (updatedFields as any).responsible_region_id = currentValue.id; isFormDirty = true; }
            } else if (key === 'responsibleCountry') {
              const initialIds = (initialValue || []).map((c: Country) => c.id).sort();
              const currentIds = (currentValue || []).map((c: Country) => c.id).sort();
              if (JSON.stringify(initialIds) !== JSON.stringify(currentIds)) { (updatedFields as any).responsible_country_ids = currentIds; isFormDirty = true; }
            } else if (key === 'endDate') {
              // Compare using the same calendar-date-to-UTC-string encoding to avoid timezone shifts
              const initialFormattedEndDate = this.formatCalendarDateToUtcString(this.initialFormState.endDate);
              if (initialFormattedEndDate !== formattedEndDate) { (updatedFields as any).active_till = formattedEndDate; isFormDirty = true; }
            } else if (key === 'status') {
              const apiStatus = this.mapUiStatusToApi(currentValue);
              const apiInitial = this.mapUiStatusToApi(initialValue);
              if (apiStatus !== apiInitial) { (updatedFields as any).status = apiStatus; isFormDirty = true; }
            } else if (key === 'email') {
              if (initialValue !== currentValue) { (updatedFields as any).email = currentValue; isFormDirty = true; }
            } else if (initialValue !== currentValue) {
              const apiFieldName = { firstName: 'first_name', lastName: 'last_name', companyName: 'company_name' }[key];
              if (apiFieldName) { (updatedFields as any)[apiFieldName] = currentValue; isFormDirty = true; }
            }
          }
        }

        // Add certificate URL to updated fields if it changed
        if (certificateUrl && certificateUrl !== this.userData.certificate_url) {
          (updatedFields as any).certificate_url = certificateUrl;
          const bucketMatch = /\/b\/([^/]+)\//.exec(certificateUrl);
          const bucket = bucketMatch?.[1] || '';
          (updatedFields as any).certificate_data = { bucket, name: uploadedPath || '' };
          isFormDirty = true;
        }

        if (!isFormDirty) {
          this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected to update.', life: 3000 });
          this.isSubmitting = false;
          this.close();
          return;
        }

        this.apiService.updateUser(this.userData.id, updatedFields).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: (response: any) => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: `User ${formValue.firstName} ${formValue.lastName} updated successfully!`, life: 5000 });
            this.userUpdated.emit({ isSuccess: true, message: 'User Updated Successfully', updatedUser: response.user });
            this.close();
          },
          error: (error: HttpErrorResponse) => {
            console.error('Failed to update user:', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error?.message || 'Failed to update user. Please try again.', life: 5000 });
            this.isSubmitting = false;
          },
        });
      } else {
        // CREATE MODE
        const payload: SignupRequestPayload = {
          first_name: formValue.firstName,
          last_name: formValue.lastName,
          email: formValue.email,
          role_id: formValue.roleType.id,
          country_id: formValue.userCountry.id,
          responsible_region_id: formValue.responsibleRegion.id,
          responsible_country_ids: formValue.responsibleCountry.map((c: Country) => c.id),
          company_name: formValue.companyName,
          status: this.mapUiStatusToApi(formValue.status),
          certificate_url: certificateUrl,
          certificate_data: { bucket: ((/\/b\/([^/]+)\//.exec(certificateUrl) || [])[1]) || '', name: uploadedPath },
          // Send null when user did not select an end date
          end_date: formattedEndDate
        } as any;

        this.apiService.createUser(payload).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: (response: any) => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: `User ${payload.first_name} ${payload.last_name} created successfully!`, life: 5000 });
            // Emit the created user object so parent can update its list without re-fetching
            const createdUser = response?.user || response;
            // Broadcast via service for app-wide subscribers
            try { this.userEvents.emitCreated(createdUser); } catch {}
            this.userCreated.emit({ isSuccess: true, message: 'New User Created Successfully', createdUser });
            this.close();
          },
          error: (error: HttpErrorResponse) => {
            console.error('Failed to create user:', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error?.message || 'Failed to create user. Please try again.', life: 5000 });
            this.isSubmitting = false;
          },
        });
      }
    } catch (error: any) {
      console.error('Error during submission:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message || 'An error occurred. Please try again.', life: 5000 });
      this.isSubmitting = false;
    }
  }

  close(): void {
    this.resetForm();
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.closeDialog.emit();
  }

  private resetForm(): void {
    if (!this.userForm) {
      return;
    }
    this.formSubmitted = false;
    this.isSubmitting = false;
    this.userForm.reset();
    this.userForm.get('roleType')?.setValue(null);
    this.userForm.get('userCountry')?.setValue(null);
    this.userForm.get('responsibleRegion')?.setValue(null);
    this.userForm.get('responsibleCountry')?.setValue([]);
    this.userForm.get('endDate')?.setValue(null);
    this.userForm.get('responsibleCountry')?.disable();
    this.filteredResponsibleCountries = [];
    this.initialFormState = null;
    this.uploadedFile = null;
    this.uploadedPath = null;
  }
  // Add this method to your UserFormComponent class
preventNumbers(event: KeyboardEvent): void {
  // Check if the pressed key is a number (0-9)
  if (/\d/.test(event.key)) {
    // Prevent the default action (typing the number)
    event.preventDefault();
  }
}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public openCertificateViewer(): void {
    // Prefer backend-provided attachments URL (already points to attachments/download/...)
    const attachmentsUrlFromApi: string | null = ((this.userData as any)?.certificate_info?.url) || null;
    if (attachmentsUrlFromApi) {
      const fname = this.extractFileNameFromUrl(attachmentsUrlFromApi);
      this.viewCertificateEvent.emit({ url: attachmentsUrlFromApi, fileName: fname });
      return;
    }

    // Fallbacks to local Firebase URL (uploaded or stored)
    const sourceUrl = this.uploadedFile?.url || this.userData?.certificate_url || '';
    if (!sourceUrl) {
      this.messageService.add({ severity: 'warn', summary: 'Unavailable', detail: 'No certificate to view.' });
      return;
    }

    let eventUrl = sourceUrl;
    let fileName = this.extractFileNameFromUrl(sourceUrl);

    // If this looks like a Firebase download URL, convert to attachments/download
    const bucketMatch = /\/b\/([^/]+)\//.exec(sourceUrl);
    let objectPath = this.uploadedPath || '';
    if (!objectPath) {
      const re = /\/o\/([^?]+)/; // captures encoded object path after /o/
      const captured = re.exec(sourceUrl)?.[1];
      if (captured) objectPath = decodeURIComponent(captured);
    }
    const bucket = bucketMatch?.[1];
    if (bucket && objectPath) {
      const base = getApiUrl('ATTACHMENTS_DOWNLOAD');
      eventUrl = `${base}/${bucket}/${encodeURIComponent(objectPath)}`;
      fileName = this.extractFileNameFromUrl(objectPath);
    }

    this.viewCertificateEvent.emit({ url: eventUrl, fileName });
  }

  validateEndDate(date: Date): void {
    if (!(date instanceof Date)) return;
    const chosen = new Date(date);
    chosen.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (chosen <= today) {
      this.userForm.get('endDate')?.setValue(null);
      this.messageService.add({ severity: 'warn', summary: 'Invalid Date', detail: 'Please select a future date.', life: 3000 });
    }
  }

  // Clear end date explicitly from the UI
  clearEndDate(): void {
    this.userForm.get('endDate')?.setValue(null);
    // mark touched so any validators or UI updates respond
    this.userForm.get('endDate')?.markAsUntouched();
    this.userForm.get('endDate')?.updateValueAndValidity();
  }

  // Parse API date strings into Date objects robustly.
  // If the string lacks timezone info (no 'Z' or offset), treat it as UTC by appending 'Z'.
  private parseApiDateToDate(dateInput: string | number): Date | null {
    if (dateInput === null || dateInput === undefined) return null;
    try {
      if (typeof dateInput === 'number') {
        return new Date(dateInput);
      }
      let s = String(dateInput).trim();
      // If it's ISO-like without timezone (e.g. 2025-09-07T18:30:00), append 'Z' to treat as UTC
      const hasTimezone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(s);
      if (!hasTimezone && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:?\d{0,2}/.test(s)) {
        s = s + 'Z';
      }
      const d = new Date(s);
      if (isNaN(d.getTime())) return null;
      return d;
    } catch (e) {
      return null;
    }
  }

  // Convert a calendar input (Date or ISO string) into a UTC midnight string: YYYY-MM-DDTHH:mm:ssZ
  private formatCalendarDateToUtcString(dateInput: any): string | undefined {
    if (!dateInput) return undefined;
    let dateObj: Date | null = null;
    if (dateInput instanceof Date) {
      // Preserve the calendar date by creating a UTC midnight for that date
      dateObj = new Date(Date.UTC(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate(), 0, 0, 0));
    } else if (typeof dateInput === 'string') {
      const parsed = this.parseApiDateToDate(dateInput);
      if (!parsed) return undefined;
      dateObj = new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 0, 0, 0));
    }
    if (!dateObj) return undefined;
    const transformed = this.datePipe.transform(dateObj, 'yyyy-MM-ddTHH:mm:ss', 'UTC');
    return transformed ? transformed + 'Z' : undefined;
  }

  // Helper to extract path from a Firebase Storage download URL (copied from signup/manage-assessment)
  private extractPathFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split("/");
      const objectPathIndex = pathSegments.indexOf("o");
      if (objectPathIndex > -1 && pathSegments.length > objectPathIndex + 1) {
        const encodedPath = pathSegments.slice(objectPathIndex + 1).join("/");
        return decodeURIComponent(encodedPath);
      }
    } catch (e) {
      console.warn("Could not parse URL to extract path:", url, e);
    }
    return null;
  }

  // Helper to extract bucket name from a Firebase Storage download URL (copied from signup/manage-assessment)
  private extractBucketFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      // Pathname looks like /v0/b/<bucket_name>/o/...
      const pathSegments = urlObj.pathname.split("/");
      const bucketIndex = pathSegments.indexOf("b");
      if (bucketIndex > -1 && pathSegments.length > bucketIndex + 1) {
        return pathSegments[bucketIndex + 1];
      }
    } catch (e) {
      console.warn("Could not parse URL to extract bucket:", url, e);
    }
    return null;
  }
}